import React, { useState, useEffect } from 'react';
import _get from 'lodash/get';
import { client } from 'cccisd-apollo';
import Loader from 'cccisd-loader';
import Table from 'cccisd-table';
import classListQuery from './classList.graphql';
import studentDataQuery from './studentData.graphql';
import Viz from './Viz';
import style from './style.css';

function PeerNomTab({ renderTimepointPills, currTimepoint }) {
    const [isLoading, setIsLoading] = useState(true);
    const [currClassId, setCurrClassId] = useState('');
    const [studentData, setStudentData] = useState(null);
    const [isLoadingStudentData, setIsLoadingStudentData] = useState(false);
    const [schoolList, setSchoolList] = useState({});
    const [classInfo, setClassInfo] = useState({});

    useEffect(() => {
        async function getClassList() {
            setIsLoading(true);
            const resp = await client.query({ query: classListQuery, fetchPolicy: 'network-only' });
            const siteClassMapper = {};
            const classInfoMap = {};
            resp.data.groups.classList.forEach(c => {
                const school = c?.ancestorGroups?.site?.group?.label;
                if (!school) {
                    return;
                }

                if (!(school in siteClassMapper)) {
                    siteClassMapper[school] = [];
                }

                siteClassMapper[school].push({ label: c.group.label, id: c.group.groupId });
                classInfoMap[c.group.groupId] = c;
            });

            setSchoolList(siteClassMapper);
            setClassInfo(classInfoMap);
            setIsLoading(false);
        }

        getClassList();
    }, []);

    useEffect(() => {
        async function getStudentData() {
            setIsLoadingStudentData(true);
            const resp = await client.query({
                query: studentDataQuery,
                fetchPolicy: 'network-only',
                variables: {
                    classId: currClassId,
                    timepoint: currTimepoint,
                    // needed so we fetch data from scoring deployments instead of progress deployments
                    // TO-DO: make all data save against progress deployments??
                    deploymentHandle: 'pnscoring_' + currTimepoint,
                },
            });

            const allLearners = _get(resp, 'data.roles.learnerList', []) || [];
            setStudentData(allLearners);
            setIsLoadingStudentData(false);
        }

        if (currClassId) {
            getStudentData();
        } else if (!currClassId && Object.keys(schoolList).length >= 0) {
            const firstSchoolWithClasses = Object.keys(schoolList).find(schoolLabel => {
                return Array.isArray(schoolList[schoolLabel]) && schoolList[schoolLabel].length >= 0;
            });
            if (firstSchoolWithClasses) {
                setCurrClassId(parseInt(schoolList[firstSchoolWithClasses][0].id, 10));
            }
        }
    }, [currTimepoint, currClassId, schoolList]);

    function renderInfo(msg) {
        return (
            <div className={'alert alert-info ' + style.alert}>
                <p className="mb-0 text-info-emphasis">{msg}</p>
            </div>
        );
    }

    function renderViz() {
        if (!currClassId || !(currClassId in classInfo)) {
            return renderInfo('Please select a Class.');
        }

        if (!studentData || isLoadingStudentData) {
            return <Loader loading type="inline" />;
        }

        const currClassInfo = classInfo[currClassId];
        const allSessions = _get(currClassInfo, 'selectedAssignmentPlan.sessionList', []) || [];
        const selectedSession = allSessions.find(sess => parseInt(sess.timepoint, 10) === parseInt(currTimepoint, 10));

        const isSessionExists = !!selectedSession;
        if (!isSessionExists) {
            return renderInfo(
                <>
                    No data collection scheduled for <b>{currClassInfo.group.label}</b> in <b>Time {currTimepoint}</b>.
                </>
            );
        }

        const isSessionStarted = selectedSession.opensDaysAgo >= 0;
        if (!isSessionStarted) {
            return renderInfo(
                <>
                    Data collection for <b>{currClassInfo.group.label}</b> in <b>Time {currTimepoint}</b> will begin{' '}
                    <b>{selectedSession.opensDate}</b>.
                </>
            );
        }

        if (!selectedSession.settings.options.some(surveyHandle => surveyHandle.startsWith('pn'))) {
            return renderInfo(
                <>
                    <b>{currClassInfo.group.label}</b> does not have Peer Nomination surveys scheduled for{' '}
                    <b>Time {currTimepoint}</b>.
                </>
            );
        }

        const allStarted = studentData.filter(student => !!student?.completionStatus?.started);
        const allComplete = studentData.filter(student => !!student?.completionStatus?.completed);
        let isSeventyPercentComplete = false;
        let pnLockWhiteList = [];
        try {
            pnLockWhiteList = JSON.parse(currClassInfo.fields.pn_locks).find(
                pnLock => parseInt(pnLock.timepoint, 10) === parseInt(currTimepoint, 10)
            ).locked;
            isSeventyPercentComplete = allComplete.length / pnLockWhiteList.length > 0.7;
        } catch (e) {
            pnLockWhiteList = [];
            // if no PN lock, it might be because no student has started yet
            if (allStarted.length === 0) {
                isSeventyPercentComplete = true;
            } else {
                isSeventyPercentComplete = false;
            }
        }

        if (!isSeventyPercentComplete) {
            return renderInfo(
                <>
                    Classwide completion for <b>{currClassInfo.group.label}</b> in <b>Time {currTimepoint}</b> is below
                    70%. It must be above 70% to show data.
                </>
            );
        }

        const isDemographicFilter = _get(currClassInfo, 'ancestorGroups.groupingUnit.fields.vizcontrols', false);
        return (
            studentData && (
                <Viz
                    pnLockWhiteList={pnLockWhiteList}
                    timepoint={currTimepoint}
                    studentData={studentData}
                    isDemographicFilter={isDemographicFilter}
                />
            )
        );
    }

    function renderTable() {
        if (isLoadingStudentData || !Array.isArray(studentData)) {
            return <span />;
        }

        return (
            <div className="mt-5 mb-2">
                <Table
                    name={'pn_data_' + currClassId}
                    columns={[
                        { name: 'studentId', label: 'Student ID', sort: true, filter: true },
                        { name: 'firstName', label: 'First Name', sort: true, filter: true },
                        { name: 'lastName', label: 'Last Name', sort: true, filter: true },
                        {
                            name: 'status',
                            label: `Time ${currTimepoint} Status`,
                            sort: true,
                            filter: true,
                            filterSettings: {
                                type: 'selectbox',
                                options: [
                                    { label: 'Not Started', value: 'Not Started' },
                                    { label: 'In Progress', value: 'In Progress' },
                                    { label: 'Complete', value: 'Complete' },
                                ],
                            },
                        },
                        {
                            name: 'lastLoginDate',
                            label: 'Last Login',
                            sort: true,
                            filter: true,
                            filterSettings: {
                                type: 'date',
                            },
                        },
                    ]}
                    data={studentData.map(student => {
                        return {
                            studentId: student.fields.studentId,
                            firstName: student.user.firstName,
                            lastName: student.user.lastName,
                            status: student.completionStatus.status || 'Not Started',
                            lastLoginDate: student.user.lastLoginDate,
                        };
                    })}
                    showPerPageOptions={false}
                    perPage={100}
                    rowKey="studentId"
                    showRowActions={false}
                    noRecordsMessage="There are no students in the selected Class"
                />
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className={style.loader}>
                <Loader loading type="inline" />
            </div>
        );
    }

    return (
        <div>
            {renderTimepointPills()}
            <div className="mb-5">
                <label htmlFor="selectedClass" className="form-label">
                    Class
                </label>
                <select
                    value={currClassId}
                    name="selectedClass"
                    id="selectedClass"
                    onChange={e => setCurrClassId(parseInt(e.target.value, 10))}
                    className={'form-select ' + style.classPicker}
                >
                    {Object.keys(schoolList).map(school => {
                        const classes = Array.isArray(schoolList[school]) ? schoolList[school] : [];
                        return (
                            <optgroup key={school} label={school}>
                                {classes.map(c => (
                                    <option value={c.id} key={c.id}>
                                        {c.label}
                                    </option>
                                ))}
                            </optgroup>
                        );
                    })}
                </select>
            </div>
            {renderViz()}
            {renderTable()}
        </div>
    );
}

export default PeerNomTab;
